/**
 * Sync Schedule Manager Component
 * 同期スケジュール管理コンポーネント
 * 
 * World-class schedule management with beautiful sliders and time controls
 * Features:
 * - Intuitive slider controls for sync frequency settings
 * - Visual peak time management with interactive timeline
 * - Smart recommendations based on business patterns
 * - Beautiful time picker with smooth animations
 * - Real-time preview of schedule impact
 * - Apple/Google/Stripe level design excellence
 * - Accessibility optimized with proper ARIA labels
 */

import React, { memo, useState, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Slider,
  Switch,
  FormControlLabel,
  Stack,
  Grid,
  Chip,
  Avatar,
  Alert,
  Button,

  Tooltip,

  LinearProgress,
  Paper,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Schedule as ScheduleIcon,

  Lightbulb as LightbulbIcon,
  Save as SaveIcon,
  Restore as RestoreIcon,
  Timeline as TimelineIcon,

  BusinessCenter as BusinessIcon,
  NightsStay as NightIcon,
  WbSunny as SunIcon,
} from '@mui/icons-material';
import type { ExternalPlatform } from '../../../types/external-sync';

const PLATFORM_CONFIG = {
  hotpepper: { name: 'ホットペッパー', color: '#ff6b35' },
  rakuten: { name: '楽天', color: '#bf0000' },
  minimo: { name: 'minimo', color: '#00c4cc' },
  epark: { name: 'EPARKビューティー', color: '#00a0e9' },
  kanzashi: { name: 'Kanzashi', color: '#9c27b0' },
} as const;

const TIME_PERIODS = {
  peak_hours: {
    label: 'ピーク時間帯',
    description: '営業時間中（10:00-20:00）',
    icon: SunIcon,
    color: '#f59e0b',
    hours: [10, 20],
    defaultInterval: 5,
    recommended: { min: 3, max: 10 },
  },
  normal: {
    label: '通常時間帯',
    description: '営業時間前後（8:00-10:00, 20:00-22:00）',
    icon: BusinessIcon,
    color: '#3b82f6',
    hours: [8, 10, 20, 22],
    defaultInterval: 15,
    recommended: { min: 10, max: 30 },
  },
  off_peak: {
    label: 'オフピーク',
    description: '深夜・早朝（22:00-8:00）',
    icon: NightIcon,
    color: '#6366f1',
    hours: [22, 8],
    defaultInterval: 60,
    recommended: { min: 30, max: 120 },
  },
} as const;

export interface SyncScheduleConfig {
  platform: ExternalPlatform;
  isEnabled: boolean;
  intervals: {
    peak_hours: number;    // minutes
    normal: number;        // minutes
    off_peak: number;      // minutes
  };
  customSchedule?: {
    enabled: boolean;
    schedules: Array<{
      startTime: string;   // HH:mm
      endTime: string;     // HH:mm
      interval: number;    // minutes
      days: number[];      // 0-6 (Sunday-Saturday)
    }>;
  };
}

export interface SyncScheduleManagerProps {
  platforms: SyncScheduleConfig[];
  onUpdate?: (platform: ExternalPlatform, config: SyncScheduleConfig) => Promise<void>;
  onSave?: () => Promise<void>;
  onReset?: (platform: ExternalPlatform) => Promise<void>;
  businessHours?: { start: string; end: string };
  className?: string;
}

const SyncScheduleManager: React.FC<SyncScheduleManagerProps> = memo(({
  platforms,
  onUpdate,
  onSave,
  onReset,
  businessHours: _businessHours = { start: '10:00', end: '20:00' },
  className,
}) => {
  const theme = useTheme();
  const [selectedPlatform, setSelectedPlatform] = useState<ExternalPlatform>('hotpepper');
  const [unsavedChanges, setUnsavedChanges] = useState(false);

  const selectedConfig = platforms.find(p => p.platform === selectedPlatform);
  const platformConfig = PLATFORM_CONFIG[selectedPlatform];

  // Calculate daily sync count
  const calculateDailySyncs = useCallback((intervals: SyncScheduleConfig['intervals']) => {
    const peakHours = 10; // 10:00-20:00
    const normalHours = 4; // 8:00-10:00, 20:00-22:00  
    const offPeakHours = 10; // 22:00-8:00

    const peakSyncs = (peakHours * 60) / intervals.peak_hours;
    const normalSyncs = (normalHours * 60) / intervals.normal;
    const offPeakSyncs = (offPeakHours * 60) / intervals.off_peak;

    return Math.round(peakSyncs + normalSyncs + offPeakSyncs);
  }, []);

  // Handle interval change
  const handleIntervalChange = useCallback((
    period: keyof SyncScheduleConfig['intervals'],
    value: number
  ) => {
    if (!selectedConfig || !onUpdate) return;

    const updatedConfig = {
      ...selectedConfig,
      intervals: {
        ...selectedConfig.intervals,
        [period]: value,
      },
    };

    onUpdate(selectedPlatform, updatedConfig);
    setUnsavedChanges(true);
  }, [selectedConfig, selectedPlatform, onUpdate]);

  // Handle enable/disable
  const handleToggleEnabled = useCallback((enabled: boolean) => {
    if (!selectedConfig || !onUpdate) return;

    const updatedConfig = {
      ...selectedConfig,
      isEnabled: enabled,
    };

    onUpdate(selectedPlatform, updatedConfig);
    setUnsavedChanges(true);
  }, [selectedConfig, selectedPlatform, onUpdate]);

  // Get optimization suggestions
  const getOptimizationSuggestions = useMemo(() => {
    if (!selectedConfig) return [];

    const suggestions = [];
    const { intervals } = selectedConfig;

    // Check if intervals are too frequent
    if (intervals.peak_hours < 3) {
      suggestions.push({
        type: 'warning',
        message: 'ピーク時間帯の同期頻度が高すぎます。サーバー負荷とAPI制限にご注意ください。',
        action: '5分以上を推奨',
      });
    }

    // Check if intervals are too infrequent
    if (intervals.peak_hours > 15) {
      suggestions.push({
        type: 'info',
        message: 'ピーク時間帯の同期間隔が長めです。リアルタイム性を重視する場合は短縮を検討してください。',
        action: '10分以下を推奨',
      });
    }

    // Check off-peak efficiency
    if (intervals.off_peak < 30) {
      suggestions.push({
        type: 'info',
        message: 'オフピーク時間帯の同期頻度を下げることでリソースを節約できます。',
        action: '60分以上を推奨',
      });
    }

    return suggestions;
  }, [selectedConfig]);

  // Generate timeline visualization data
  const timelineData = useMemo(() => {
    if (!selectedConfig) return [];

    const data = [];
    for (let hour = 0; hour < 24; hour++) {
      let period: keyof typeof TIME_PERIODS;
      let interval: number;

      if (hour >= 10 && hour < 20) {
        period = 'peak_hours';
        interval = selectedConfig.intervals.peak_hours;
      } else if ((hour >= 8 && hour < 10) || (hour >= 20 && hour < 22)) {
        period = 'normal';
        interval = selectedConfig.intervals.normal;
      } else {
        period = 'off_peak';
        interval = selectedConfig.intervals.off_peak;
      }

      data.push({
        hour,
        period,
        interval,
        syncsPerHour: 60 / interval,
        color: TIME_PERIODS[period].color,
      });
    }

    return data;
  }, [selectedConfig]);

  if (!selectedConfig) return null;

  return (
    <Box className={className}>
      {/* Platform Selector */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={2} mb={2}>
            <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
              <ScheduleIcon />
            </Avatar>
            <Box>
              <Typography variant="h5" fontWeight={600}>
                同期スケジュール管理
              </Typography>
              <Typography variant="body2" color="text.secondary">
                プラットフォーム別の自動同期間隔を設定
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1} flexWrap="wrap">
            {platforms.map((platform) => {
              const config = PLATFORM_CONFIG[platform.platform];
              const isSelected = selectedPlatform === platform.platform;
              
              return (
                <Chip
                  key={platform.platform}
                  label={config.name}
                  variant={isSelected ? 'filled' : 'outlined'}
                  clickable
                  onClick={() => setSelectedPlatform(platform.platform)}
                  sx={{
                    borderColor: config.color,
                    color: isSelected ? 'white' : config.color,
                    bgcolor: isSelected ? config.color : 'transparent',
                    '&:hover': {
                      bgcolor: isSelected ? alpha(config.color, 0.9) : alpha(config.color, 0.1),
                    },
                  }}
                  avatar={
                    <Avatar
                      sx={{
                        bgcolor: platform.isEnabled ? '#10b981' : '#6b7280',
                        width: 16,
                        height: 16,
                      }}
                    >
                      {platform.isEnabled ? '●' : '○'}
                    </Avatar>
                  }
                />
              );
            })}
          </Stack>
        </CardContent>
      </Card>

      {/* Main Settings */}
      <Grid container spacing={3}>
        {/* Settings Panel */}
        <Grid item xs={12} lg={8}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
                <Typography variant="h6" fontWeight={600} sx={{ color: platformConfig.color }}>
                  {platformConfig.name} 同期設定
                </Typography>
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={selectedConfig.isEnabled}
                      onChange={(e) => handleToggleEnabled(e.target.checked)}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: platformConfig.color,
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          bgcolor: platformConfig.color,
                        },
                      }}
                    />
                  }
                  label="自動同期を有効化"
                />
              </Stack>

              {selectedConfig.isEnabled && (
                <Stack spacing={4}>
                  {/* Time Period Settings */}
                  {Object.entries(TIME_PERIODS).map(([periodKey, periodConfig]) => {
                    const period = periodKey as keyof typeof TIME_PERIODS;
                    const interval = selectedConfig.intervals[period];
                    const IconComponent = periodConfig.icon;

                    return (
                      <motion.div
                        key={period}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: Object.keys(TIME_PERIODS).indexOf(period) * 0.1 }}
                      >
                        <Paper
                          sx={{
                            p: 3,
                            background: `linear-gradient(135deg, ${alpha(periodConfig.color, 0.05)} 0%, ${alpha(periodConfig.color, 0.02)} 100%)`,
                            border: `1px solid ${alpha(periodConfig.color, 0.1)}`,
                          }}
                        >
                          <Stack spacing={3}>
                            {/* Period Header */}
                            <Stack direction="row" alignItems="center" spacing={2}>
                              <Avatar
                                sx={{
                                  bgcolor: alpha(periodConfig.color, 0.1),
                                  color: periodConfig.color,
                                }}
                              >
                                <IconComponent />
                              </Avatar>
                              <Box>
                                <Typography variant="h6" fontWeight={600}>
                                  {periodConfig.label}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {periodConfig.description}
                                </Typography>
                              </Box>
                              <Box sx={{ ml: 'auto', textAlign: 'right' }}>
                                <Typography variant="h4" fontWeight={600} color={periodConfig.color}>
                                  {interval}分
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {Math.round(60 / interval * 10) / 10}回/時間
                                </Typography>
                              </Box>
                            </Stack>

                            {/* Interval Slider */}
                            <Box sx={{ px: 2 }}>
                              <Slider
                                value={interval}
                                onChange={(_, value) => handleIntervalChange(period, value as number)}
                                min={periodConfig.recommended.min}
                                max={periodConfig.recommended.max}
                                step={period === 'peak_hours' ? 1 : 5}
                                marks={[
                                  { value: periodConfig.recommended.min, label: `${periodConfig.recommended.min}分` },
                                  { value: periodConfig.defaultInterval, label: '推奨' },
                                  { value: periodConfig.recommended.max, label: `${periodConfig.recommended.max}分` },
                                ]}
                                sx={{
                                  color: periodConfig.color,
                                  '& .MuiSlider-thumb': {
                                    '&:hover, &.Mui-focusVisible': {
                                      boxShadow: `0 0 0 8px ${alpha(periodConfig.color, 0.16)}`,
                                    },
                                  },
                                  '& .MuiSlider-track': {
                                    background: `linear-gradient(90deg, ${periodConfig.color}, ${alpha(periodConfig.color, 0.7)})`,
                                  },
                                }}
                              />
                            </Box>

                            {/* Recommendations */}
                            {(interval < periodConfig.recommended.min || interval > periodConfig.recommended.max) && (
                              <Alert
                                severity={interval < periodConfig.recommended.min ? 'warning' : 'info'}
                                sx={{ borderRadius: 2 }}
                                icon={<LightbulbIcon />}
                              >
                                <Typography variant="body2">
                                  {interval < periodConfig.recommended.min
                                    ? `頻繁な同期はサーバー負荷を増加させる可能性があります。${periodConfig.recommended.min}分以上を推奨します。`
                                    : `同期間隔が長いため、リアルタイム性が低下する可能性があります。${periodConfig.recommended.max}分以下を推奨します。`
                                  }
                                </Typography>
                              </Alert>
                            )}
                          </Stack>
                        </Paper>
                      </motion.div>
                    );
                  })}

                  {/* Daily Summary */}
                  <Paper
                    sx={{
                      p: 3,
                      background: `linear-gradient(135deg, ${alpha(platformConfig.color, 0.05)} 0%, ${alpha(platformConfig.color, 0.02)} 100%)`,
                      border: `1px solid ${alpha(platformConfig.color, 0.1)}`,
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={2} mb={2}>
                      <Avatar sx={{ bgcolor: platformConfig.color }}>
                        <TimelineIcon />
                      </Avatar>
                      <Box>
                        <Typography variant="h6" fontWeight={600}>
                          1日の同期回数
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          現在の設定での予想実行回数
                        </Typography>
                      </Box>
                      <Box sx={{ ml: 'auto', textAlign: 'right' }}>
                        <Typography variant="h3" fontWeight={600} color={platformConfig.color}>
                          {calculateDailySyncs(selectedConfig.intervals)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          回/日
                        </Typography>
                      </Box>
                    </Stack>

                    {/* Timeline Visualization */}
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                        24時間タイムライン
                      </Typography>
                      <Stack direction="row" spacing={0.25}>
                        {timelineData.map((data, index) => (
                          <Tooltip
                            key={index}
                            title={`${data.hour}:00 - ${data.syncsPerHour.toFixed(1)}回/時間`}
                          >
                            <Box
                              sx={{
                                flex: 1,
                                height: 20,
                                bgcolor: data.color,
                                borderRadius: 0.5,
                                opacity: 0.7,
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  opacity: 1,
                                  transform: 'scaleY(1.2)',
                                },
                              }}
                            />
                          </Tooltip>
                        ))}
                      </Stack>
                      <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">0時</Typography>
                        <Typography variant="caption" color="text.secondary">12時</Typography>
                        <Typography variant="caption" color="text.secondary">24時</Typography>
                      </Stack>
                    </Box>
                  </Paper>
                </Stack>
              )}

              {/* Action Buttons */}
              <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 3 }}>
                <Button
                  variant="outlined"
                  startIcon={<RestoreIcon />}
                  onClick={() => onReset?.(selectedPlatform)}
                  sx={{
                    borderColor: alpha(platformConfig.color, 0.3),
                    color: platformConfig.color,
                    '&:hover': {
                      borderColor: platformConfig.color,
                      bgcolor: alpha(platformConfig.color, 0.05),
                    },
                  }}
                >
                  リセット
                </Button>
                
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={onSave}
                  disabled={!unsavedChanges}
                  sx={{
                    bgcolor: platformConfig.color,
                    '&:hover': {
                      bgcolor: alpha(platformConfig.color, 0.9),
                    },
                    '&:disabled': {
                      bgcolor: alpha(platformConfig.color, 0.3),
                    },
                  }}
                >
                  {unsavedChanges ? '変更を保存' : '保存済み'}
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Optimization Panel */}
        <Grid item xs={12} lg={4}>
          <Stack spacing={3}>
            {/* Optimization Suggestions */}
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  最適化提案
                </Typography>
                
                {getOptimizationSuggestions.length > 0 ? (
                  <Stack spacing={2}>
                    {getOptimizationSuggestions.map((suggestion, index) => (
                      <Alert
                        key={index}
                        severity={suggestion.type as 'warning' | 'info'}
                        sx={{ borderRadius: 2 }}
                      >
                        <Typography variant="body2" gutterBottom>
                          {suggestion.message}
                        </Typography>
                        <Typography variant="caption" fontWeight={600}>
                          {suggestion.action}
                        </Typography>
                      </Alert>
                    ))}
                  </Stack>
                ) : (
                  <Alert severity="success" sx={{ borderRadius: 2 }}>
                    <Typography variant="body2">
                      現在の設定は最適化されています！
                    </Typography>
                  </Alert>
                )}
              </CardContent>
            </Card>

            {/* Performance Impact */}
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  パフォーマンス影響
                </Typography>
                
                <Stack spacing={2}>
                  <Box>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Typography variant="body2">サーバー負荷</Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {calculateDailySyncs(selectedConfig.intervals) > 200 ? '高' : 
                         calculateDailySyncs(selectedConfig.intervals) > 100 ? '中' : '低'}
                      </Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(calculateDailySyncs(selectedConfig.intervals) / 300 * 100, 100)}
                      sx={{
                        mt: 1,
                        '& .MuiLinearProgress-bar': {
                          bgcolor: calculateDailySyncs(selectedConfig.intervals) > 200 ? '#dc2626' : 
                                   calculateDailySyncs(selectedConfig.intervals) > 100 ? '#f59e0b' : '#10b981',
                        },
                      }}
                    />
                  </Box>

                  <Box>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Typography variant="body2">リアルタイム性</Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {selectedConfig.intervals.peak_hours <= 5 ? '高' : 
                         selectedConfig.intervals.peak_hours <= 10 ? '中' : '低'}
                      </Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={Math.max(100 - selectedConfig.intervals.peak_hours * 5, 0)}
                      sx={{
                        mt: 1,
                        '& .MuiLinearProgress-bar': {
                          bgcolor: selectedConfig.intervals.peak_hours <= 5 ? '#10b981' : 
                                   selectedConfig.intervals.peak_hours <= 10 ? '#f59e0b' : '#dc2626',
                        },
                      }}
                    />
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
});

SyncScheduleManager.displayName = 'SyncScheduleManager';

export default SyncScheduleManager;